import React from "react";
import { useNavigate } from "react-router-dom";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import DeclrAttributeDefinitionController from "./DeclrAttributeDefinitionController";

/**
 * 🎯 ATTRIBUTE DEFINITION REGISTRY (Group ESG admin screen)
 * Define reusable, typed local-capture attributes (e.g. "Electricity
 * consumption") once, for composing into per-company local forms
 * (Form Composition) and later mapping to group taxonomy concepts
 * (Attribute Mapping).
 */
export default function DeclrAttributeDefinitionView() {
  const navigate = useNavigate();
  const siteKey = process.env.REACT_APP_SITE_KEY;

  const {
    definitions,
    unitFamilies,
    unitsForFamily,
    loading,
    saving,
    error,
    form,
    editingId,
    VALUE_DATA_TYPES,
    handleFieldChange,
    handleEdit,
    handleSave,
    handleRemove,
    resetForm,
    expandedId,
    enumOptions,
    newOption,
    setNewOption,
    toggleEnumOptions,
    handleAddEnumOption,
    handleRemoveEnumOption,
  } = DeclrAttributeDefinitionController(siteKey);

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Attribute Definitions</h2>
                <p className="text-muted small mb-0">
                  Reusable local-capture fields companies compose into their reporting forms
                </p>
              </div>
            </div>

            {error && <div className="alert alert-danger extra-small">{error}</div>}

            <div className="row g-3">
              {/* CREATE / EDIT FORM */}
              <div className="col-md-4">
                <div className="card shadow-sm border-0 bg-white p-3 h-100">
                  <h6 className="extra-small fw-bold text-uppercase text-primary mb-3">
                    {editingId ? "Edit Attribute" : "New Attribute"}
                  </h6>

                  <label className="extra-small fw-bold text-muted mb-1">Label</label>
                  <input
                    className="form-control form-control-sm mb-2"
                    value={form.Label}
                    onChange={(e) => handleFieldChange("Label", e.target.value)}
                    placeholder="e.g. Electricity consumption"
                  />

                  <label className="extra-small fw-bold text-muted mb-1">Guidance Text</label>
                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={2}
                    value={form.MetaDescription}
                    onChange={(e) => handleFieldChange("MetaDescription", e.target.value)}
                    placeholder="Shown to the user filling this field in"
                  />

                  <label className="extra-small fw-bold text-muted mb-1">Value Type</label>
                  <select
                    className="form-select form-select-sm mb-2"
                    value={form.ValueDataType}
                    onChange={(e) => handleFieldChange("ValueDataType", e.target.value)}
                  >
                    {VALUE_DATA_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>

                  {form.ValueDataType === "numeric" && (
                    <>
                      <label className="extra-small fw-bold text-muted mb-1">Allowed Unit Family</label>
                      <select
                        className="form-select form-select-sm mb-2"
                        value={form.AllowedUnitFamily}
                        onChange={(e) => {
                          handleFieldChange("AllowedUnitFamily", e.target.value);
                          handleFieldChange("DefaultUnitCode", "");
                        }}
                      >
                        <option value="">-- none --</option>
                        {unitFamilies.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>

                      {form.AllowedUnitFamily && (
                        <>
                          <label className="extra-small fw-bold text-muted mb-1">Default Unit</label>
                          <select
                            className="form-select form-select-sm mb-2"
                            value={form.DefaultUnitCode}
                            onChange={(e) => handleFieldChange("DefaultUnitCode", e.target.value)}
                          >
                            <option value="">-- select --</option>
                            {unitsForFamily(form.AllowedUnitFamily).map((u) => {
                              const code = GoldenSourceHelper.get(u, "UnitCode");
                              return (
                                <option key={code} value={code}>
                                  {GoldenSourceHelper.get(u, "UnitLabel")} ({code})
                                </option>
                              );
                            })}
                          </select>
                        </>
                      )}
                    </>
                  )}

                  <label className="extra-small fw-bold text-muted mb-1">Category</label>
                  <input
                    className="form-control form-control-sm mb-3"
                    value={form.CategoryTag}
                    onChange={(e) => handleFieldChange("CategoryTag", e.target.value)}
                    placeholder="e.g. Transport, Energy"
                  />

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary fw-bold"
                      disabled={saving || !form.Label.trim()}
                      onClick={handleSave}
                    >
                      {saving ? "SAVING..." : editingId ? "SAVE CHANGES" : "CREATE"}
                    </button>
                    {editingId && (
                      <button className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* LIST */}
              <div className="col-md-8">
                <div className="card shadow-sm border-0 bg-white overflow-hidden">
                  <div className="table-responsive" style={{ maxHeight: 560 }}>
                    <table className="table table-hover table-sm mb-0 align-middle">
                      <tbody>
                        {loading && (
                          <tr><td className="p-4 text-center text-muted extra-small italic">Loading...</td></tr>
                        )}
                        {!loading && definitions.length === 0 && (
                          <tr><td className="p-4 text-center text-muted extra-small italic">No attribute definitions yet.</td></tr>
                        )}
                        {definitions.map((def, i) => {
                          const id = GoldenSourceHelper.get(def, "AttributeDefinitionId");
                          const valueType = GoldenSourceHelper.get(def, "ValueDataType");
                          return (
                            <React.Fragment key={id || i}>
                              <tr>
                                <td className="ps-3">
                                  <div className="fw-bold extra-small">{GoldenSourceHelper.get(def, "Label")}</div>
                                  <div className="extra-small text-muted">
                                    {GoldenSourceHelper.get(def, "CategoryTag") || "uncategorized"} &middot; {valueType}
                                    {GoldenSourceHelper.get(def, "DefaultUnitCode") && (
                                      <> &middot; {GoldenSourceHelper.get(def, "DefaultUnitCode")}</>
                                    )}
                                  </div>
                                </td>
                                <td className="text-end pe-3" style={{ width: 220 }}>
                                  {valueType === "enum" && (
                                    <button
                                      className="btn btn-sm btn-outline-secondary fw-bold me-1"
                                      onClick={() => toggleEnumOptions(id)}
                                    >
                                      {expandedId === id ? "HIDE OPTIONS" : "OPTIONS"}
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-primary fw-bold me-1"
                                    onClick={() => handleEdit(def)}
                                  >
                                    EDIT
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger fw-bold"
                                    onClick={() => handleRemove(id)}
                                  >
                                    REMOVE
                                  </button>
                                </td>
                              </tr>
                              {expandedId === id && (
                                <tr>
                                  <td colSpan={2} className="bg-light ps-4 py-2">
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                      {enumOptions.map((opt) => {
                                        const optId = GoldenSourceHelper.get(opt, "OptionId");
                                        return (
                                          <span key={optId} className="badge bg-white border text-dark extra-small d-flex align-items-center gap-2">
                                            {GoldenSourceHelper.get(opt, "OptionLabel")}
                                            <i
                                              className="bi bi-x-circle text-danger"
                                              style={{ cursor: "pointer" }}
                                              onClick={() => handleRemoveEnumOption(optId, id)}
                                            ></i>
                                          </span>
                                        );
                                      })}
                                      {enumOptions.length === 0 && (
                                        <span className="extra-small text-muted italic">No options yet.</span>
                                      )}
                                    </div>
                                    <div className="d-flex gap-2">
                                      <input
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: 160 }}
                                        placeholder="Label"
                                        value={newOption.OptionLabel}
                                        onChange={(e) => setNewOption((p) => ({ ...p, OptionLabel: e.target.value }))}
                                      />
                                      <input
                                        className="form-control form-control-sm"
                                        style={{ maxWidth: 160 }}
                                        placeholder="Value"
                                        value={newOption.OptionValue}
                                        onChange={(e) => setNewOption((p) => ({ ...p, OptionValue: e.target.value }))}
                                      />
                                      <button
                                        className="btn btn-sm btn-primary fw-bold"
                                        onClick={() => handleAddEnumOption(id)}
                                      >
                                        ADD
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/site-admin")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Site Administration
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
